import * as React from "react";
import { NavLink } from "react-router-dom";
import { cn } from "@eduviaos/ui-kit";
import { CalendarClock, ClipboardList, Trash2, Users } from "lucide-react";

/**
 * AdmissionsSubNav — Phase D3/D5/D4.
 *
 * Sub-navigation strip rendered at the top of every admissions surface
 * (List, Board, Calendar, Workload, Trash) so the IA can pivot between
 * the operational lenses without round-tripping through the sidebar.
 *
 * The sidebar still has a single "Admissions" entry — Iron Rule #16
 * (information density) discourages scattering related views across
 * the primary nav. The sub-nav uses standard NavLinks so the active
 * lens is highlighted whenever the URL matches.
 */

interface SubNavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  /** When `true`, NavLink uses `end` semantics (no prefix matching). */
  end?: boolean;
}

const ITEMS: SubNavItem[] = [
  {
    to: "/admissions",
    label: "Enquiries",
    icon: <ClipboardList className="size-3.5" aria-hidden="true" />,
    end: true,
  },
  {
    to: "/admissions/calendar",
    label: "Calendar",
    icon: <CalendarClock className="size-3.5" aria-hidden="true" />,
  },
  {
    to: "/admissions/workload",
    label: "Workload",
    icon: <Users className="size-3.5" aria-hidden="true" />,
  },
  {
    to: "/admissions/trash",
    label: "Trash",
    icon: <Trash2 className="size-3.5" aria-hidden="true" />,
  },
];

export function AdmissionsSubNav() {
  return (
    <nav
      aria-label="Admissions views"
      className="border-border/60 mb-4 flex items-center gap-1 border-b text-xs"
      data-testid="admissions-subnav"
    >
      {ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cn(
              "text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 border-b-2 border-transparent px-3 py-2 font-medium transition-colors",
              isActive && "border-primary text-foreground",
            )
          }
        >
          {item.icon}
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}
